// Dependency-free table exports: CSV, Excel (SpreadsheetML — opens natively in
// Excel/Numbers/Sheets), and print-to-PDF (via the browser print dialog).
export type Cell = string | number;

function triggerDownload(name: string, mime: string, content: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const xml = (v: Cell) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** CSV with a UTF-8 BOM so Excel opens accented characters correctly. */
export function downloadCsv(filename: string, headers: string[], rows: Cell[][]) {
  const esc = (v: Cell) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
  triggerDownload(`${filename}.csv`, "text/csv;charset=utf-8", "﻿" + csv);
}

/** Excel workbook (SpreadsheetML 2003). Numeric cells are typed so Excel treats
 *  them as numbers; opens without a converter in Excel/Numbers/Google Sheets. */
export function downloadXls(filename: string, sheet: string, headers: string[], rows: Cell[][]) {
  const cell = (v: Cell) => {
    const isNum = typeof v === "number" && Number.isFinite(v);
    return `<Cell><Data ss:Type="${isNum ? "Number" : "String"}">${xml(v)}</Data></Cell>`;
  };
  const row = (cells: Cell[]) => `<Row>${cells.map(cell).join("")}</Row>`;
  const body = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Worksheet ss:Name="${xml(sheet).slice(0, 31) || "Sheet1"}"><Table>${row(headers)}${rows.map(row).join("")}</Table></Worksheet>
</Workbook>`;
  triggerDownload(`${filename}.xls`, "application/vnd.ms-excel", body);
}

/** Opens a printable window with the table styled cleanly — the user saves as PDF. */
export function printTable(title: string, headers: string[], rows: Cell[][]) {
  const w = window.open("", "_blank", "width=1000,height=700");
  if (!w) return;
  const td = (c: Cell) => `<td class="${typeof c === "number" ? "num" : ""}">${xml(c)}</td>`;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${xml(title)}</title><style>
    body{font-family:Inter,-apple-system,"Segoe UI",sans-serif;padding:28px;color:#111}
    h1{font-size:15px;margin:0 0 14px}
    table{border-collapse:collapse;width:100%;font-size:11px}
    th,td{border:1px solid #e2e2e2;padding:6px 9px;text-align:left}
    th{background:#f6f6f6;text-transform:uppercase;font-size:9px;letter-spacing:.06em;color:#555}
    td.num{text-align:right;font-variant-numeric:tabular-nums}
    @media print{body{padding:0}}
  </style></head><body><h1>${xml(title)}</h1><table><thead><tr>${headers.map((h) => `<th>${xml(h)}</th>`).join("")}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map(td).join("")}</tr>`).join("")}</tbody></table>
  <script>window.onload=function(){setTimeout(function(){window.print()},150)}</script></body></html>`);
  w.document.close();
}
